import { motion } from "motion/react";

/**
 * Hero do /conhecimento — composição 2D distinta do Painel.
 *
 * Tema: camadas translúcidas empilhadas / "estante de conhecimento" minimalista.
 * Fundo navy escuro + warm halo, com 3 retângulos translúcidos (cards de vidro)
 * flutuando em perspectiva sutil, drifting lentamente.
 *
 * Diferente do blob orgânico do Painel — aqui é mais geométrico, organizado,
 * representando o conceito de KB (camadas de conhecimento).
 */
export function HeroKnowledge() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: -1,
        background: "#050810",
        overflow: "hidden",
      }}
    >
      {/* Warm halo direita */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 50% 75% at 78% 50%, rgba(255,112,0,0.16) 0%, rgba(155,58,142,0.06) 35%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <svg
        viewBox="0 0 1600 720"
        preserveAspectRatio="xMaxYMid slice"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          {/* Gradient quente translúcido pra "vidro com luz interna" */}
          <linearGradient id="cardGlassWarm" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff8a3a" stopOpacity="0.55">
              <animate
                attributeName="stop-color"
                values="#ff8a3a;#ffaa55;#ff7030;#ff8a3a"
                dur="14s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="60%" stopColor="#9b3a8e" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1a0c28" stopOpacity="0" />
          </linearGradient>

          <linearGradient id="cardGlassCool" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a880ff" stopOpacity="0.40">
              <animate
                attributeName="stop-color"
                values="#a880ff;#7a3082;#c44b88;#a880ff"
                dur="18s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor="#0a1020" stopOpacity="0" />
          </linearGradient>

          {/* Soft blur pro back card */}
          <filter id="kbSoftBlur">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <filter id="kbHeavyBlur">
            <feGaussianBlur stdDeviation="18" />
          </filter>

          {/* Highlight gradient pra borda superior dos cards (catch light) */}
          <linearGradient id="cardEdge" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {/* Card 3 — atrás, mais blurred, drift lento */}
        <motion.g
          animate={{ x: [0, 18, -8, 0], y: [0, -12, 6, 0], rotate: [-8, -6, -10, -8] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "1380px 380px" }}
        >
          <rect
            x="1120"
            y="220"
            width="360"
            height="280"
            rx="16"
            fill="url(#cardGlassCool)"
            filter="url(#kbHeavyBlur)"
            opacity="0.75"
            transform="rotate(-8 1300 360)"
          />
        </motion.g>

        {/* Card 2 — meio, blur médio */}
        <motion.g
          animate={{ x: [0, -14, 10, 0], y: [0, 8, -6, 0], rotate: [4, 6, 2, 4] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "1320px 360px" }}
        >
          <rect
            x="1180"
            y="200"
            width="340"
            height="270"
            rx="14"
            fill="url(#cardGlassWarm)"
            filter="url(#kbSoftBlur)"
            opacity="0.85"
            transform="rotate(4 1350 335)"
          />
          {/* edge highlight */}
          <rect
            x="1180"
            y="200"
            width="340"
            height="3"
            rx="2"
            fill="url(#cardEdge)"
            opacity="0.6"
            transform="rotate(4 1350 335)"
          />
        </motion.g>

        {/* Card 1 — frente, sem blur, drift sutil */}
        <motion.g
          animate={{ x: [0, 8, -4, 0], y: [0, -4, 6, 0], rotate: [-2, 0, -3, -2] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "1280px 360px" }}
        >
          <rect
            x="1140"
            y="220"
            width="320"
            height="260"
            rx="14"
            fill="url(#cardGlassWarm)"
            opacity="0.95"
            transform="rotate(-2 1300 350)"
          />
          <rect
            x="1140"
            y="220"
            width="320"
            height="3"
            rx="2"
            fill="url(#cardEdge)"
            opacity="0.7"
            transform="rotate(-2 1300 350)"
          />
        </motion.g>

        {/* Light dust/pó dourado entre as camadas */}
        {Array.from({ length: 24 }).map((_, i) => {
          const cx = 1100 + Math.random() * 480;
          const cy = 180 + Math.random() * 380;
          const r = 1 + Math.random() * 1.5;
          const dur = 8 + Math.random() * 6;
          const delay = Math.random() * 4;
          return (
            <motion.circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="rgba(255,210,140,0.7)"
              animate={{
                y: [0, -20 - Math.random() * 30, 0],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: dur,
                delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </svg>
    </div>
  );
}
